import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  BookOpen, Users, MessageCircle, ArrowLeft, Send, Sparkles,
  User, RefreshCw, Loader2, ThumbsUp, ThumbsDown, HelpCircle, Volume2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DebatePage = ({ user }) => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  // State
  const [topic, setTopic] = useState("");
  const [topicSubmitted, setTopicSubmitted] = useState(false);
  const [suggestedPhilosophers, setSuggestedPhilosophers] = useState([]);
  const [selectedPhilosophers, setSelectedPhilosophers] = useState([]);
  const [debateMessages, setDebateMessages] = useState([]);
  const [studentQuestion, setStudentQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [debateStarted, setDebateStarted] = useState(false);
  const [debatePhase, setDebatePhase] = useState("topic"); // topic, selection, debate
  const [philosophers, setPhilosophers] = useState([]);

  useEffect(() => {
    fetchPhilosophers();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [debateMessages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchPhilosophers = async () => {
    try {
      const response = await axios.get(`${API}/philosophers`);
      setPhilosophers(response.data);
    } catch (error) {
      console.error("Error fetching philosophers:", error);
    }
  };

  const handleTopicSubmit = async () => {
    if (!topic.trim()) {
      toast.error("Veuillez entrer un sujet de débat");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/debate/suggest-philosophers`, {
        topic: topic.trim(),
        student_id: user?.id
      });
      
      setSuggestedPhilosophers(response.data.suggested_philosophers || []);
      setTopicSubmitted(true);
      setDebatePhase("selection");
      toast.success("Voici les philosophes recommandés pour ce sujet !");
    } catch (error) {
      console.error("Error getting suggestions:", error);
      toast.error("Erreur lors de la suggestion des philosophes");
    } finally {
      setLoading(false);
    }
  };

  const togglePhilosopher = (philosopher) => {
    if (selectedPhilosophers.find(p => p.id === philosopher.id)) {
      setSelectedPhilosophers(selectedPhilosophers.filter(p => p.id !== philosopher.id));
    } else if (selectedPhilosophers.length < 3) {
      setSelectedPhilosophers([...selectedPhilosophers, philosopher]);
    } else {
      toast.info("Maximum 3 philosophes pour un débat");
    }
  };

  const startDebate = async () => {
    if (selectedPhilosophers.length < 2) {
      toast.error("Sélectionnez au moins 2 philosophes");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/debate/start`, {
        topic: topic,
        philosopher_ids: selectedPhilosophers.map(p => p.id),
        student_id: user?.id,
        student_name: user ? `${user.first_name} ${user.last_name}` : "Étudiant"
      });
      
      setDebateMessages(response.data.opening_statements || []);
      setDebateStarted(true);
      setDebatePhase("debate");
      toast.success("Le débat commence !");
    } catch (error) {
      console.error("Error starting debate:", error);
      toast.error("Erreur lors du démarrage du débat");
    } finally {
      setLoading(false);
    }
  };

  const askQuestion = async () => {
    if (!studentQuestion.trim()) return;

    const question = studentQuestion.trim();
    setStudentQuestion("");
    
    // Add student question to messages
    setDebateMessages(prev => [...prev, {
      role: "student",
      name: user ? `${user.first_name} ${user.last_name}` : "Vous",
      content: question,
      timestamp: new Date().toISOString()
    }]);

    setLoading(true);
    try {
      const response = await axios.post(`${API}/debate/continue`, {
        topic: topic,
        philosopher_ids: selectedPhilosophers.map(p => p.id),
        student_question: question,
        previous_messages: debateMessages,
        student_id: user?.id
      });
      
      // Add philosopher responses
      if (response.data.responses) {
        setDebateMessages(prev => [...prev, ...response.data.responses]);
      }
    } catch (error) {
      console.error("Error continuing debate:", error);
      toast.error("Erreur lors de la continuation du débat");
    } finally {
      setLoading(false);
    }
  };

  const getPhilosopherAvatar = (philosopherId) => {
    const phil = philosophers.find(p => p.id === philosopherId);
    return phil?.avatar || null;
  };

  const getPhilosopherColor = (index) => {
    const colors = ["bg-[#1A365D]", "bg-[#D4AF37]", "bg-[#22C55E]"];
    return colors[index % colors.length];
  };

  // Topic input phase
  const renderTopicInput = () => (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="topic-input-section">
      <div className="text-center">
        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#1A365D] flex items-center justify-center">
          <MessageCircle className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-2xl font-medium text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Proposez un sujet de débat
        </h2>
        <p className="text-gray-500">
          Entrez un sujet philosophique et SophIA suggérera les meilleurs penseurs pour en débattre
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <Textarea
              placeholder="Ex: La liberté est-elle compatible avec le déterminisme ? 
Le bonheur dépend-il de nous ?
La technique nous libère-t-elle ou nous aliène-t-elle ?"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="min-h-32 text-lg"
              data-testid="topic-input"
            />
            
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-gray-500">Suggestions :</span>
              {["L'art est-il inutile ?", "Peut-on se connaître soi-même ?", "Le travail est-il une aliénation ?"].map((suggestion) => (
                <Badge
                  key={suggestion}
                  variant="outline"
                  className="cursor-pointer hover:bg-[#1A365D] hover:text-white transition-colors"
                  onClick={() => setTopic(suggestion)}
                >
                  {suggestion}
                </Badge>
              ))}
            </div>

            <Button
              onClick={handleTopicSubmit}
              disabled={loading || !topic.trim()}
              className="w-full h-12 bg-[#1A365D] hover:bg-[#132745] text-white"
              data-testid="submit-topic-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Suggérer des philosophes
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Philosopher selection phase
  const renderPhilosopherSelection = () => (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="philosopher-selection-section">
      <div className="text-center">
        <h2 className="text-2xl font-medium text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Choisissez vos philosophes
        </h2>
        <p className="text-gray-500 mb-2">Sujet : "{topic}"</p>
        <p className="text-sm text-gray-400">
          Sélectionnez 2 à 3 philosophes parmi nos recommandations
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
        <p className="text-amber-800 text-sm">
          <Sparkles className="w-4 h-4 inline mr-1" />
          <strong>Recommandation SophIA :</strong> Ces philosophes ont des perspectives particulièrement riches sur ce sujet.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {suggestedPhilosophers.map((suggestion, idx) => {
          const philosopher = philosophers.find(p => p.id === suggestion.id) || suggestion;
          const isSelected = selectedPhilosophers.find(p => p.id === suggestion.id);
          
          return (
            <Card 
              key={suggestion.id}
              className={`cursor-pointer transition-all ${
                isSelected 
                  ? 'ring-2 ring-[#1A365D] bg-blue-50' 
                  : 'hover:shadow-lg'
              }`}
              onClick={() => togglePhilosopher(suggestion)}
              data-testid={`philosopher-card-${suggestion.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  {philosopher.avatar ? (
                    <img 
                      src={philosopher.avatar} 
                      alt={suggestion.name}
                      className="w-14 h-14 rounded-full object-cover"
                    />
                  ) : (
                    <div className={`w-14 h-14 rounded-full ${getPhilosopherColor(idx)} flex items-center justify-center text-white text-xl font-bold`}>
                      {suggestion.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <CardTitle className="text-lg">{suggestion.name}</CardTitle>
                    <Badge variant="outline" className="text-xs">
                      #{idx + 1} recommandé
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-2">{suggestion.reason}</p>
                <div className="flex flex-wrap gap-1">
                  {(suggestion.key_concepts || philosopher.key_concepts || []).slice(0, 3).map((concept) => (
                    <Badge key={concept} variant="secondary" className="text-xs">
                      {concept}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Also show other philosophers */}
      <details className="mt-4">
        <summary className="cursor-pointer text-[#1A365D] font-medium">
          Voir tous les philosophes disponibles
        </summary>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
          {philosophers
            .filter(p => !suggestedPhilosophers.find(s => s.id === p.id))
            .map((phil) => {
              const isSelected = selectedPhilosophers.find(p => p.id === phil.id);
              return (
                <Button
                  key={phil.id}
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => togglePhilosopher(phil)}
                  className={isSelected ? "bg-[#1A365D]" : ""}
                >
                  {phil.name}
                </Button>
              );
            })}
        </div>
      </details>

      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Sélectionnés :</span>
          {selectedPhilosophers.map((phil, idx) => (
            <Badge key={phil.id} className={getPhilosopherColor(idx) + " text-white"}>
              {phil.name}
            </Badge>
          ))}
          {selectedPhilosophers.length === 0 && (
            <span className="text-sm text-gray-400">Aucun</span>
          )}
        </div>
        <Button
          onClick={startDebate}
          disabled={loading || selectedPhilosophers.length < 2}
          className="bg-[#22C55E] hover:bg-[#16a34a] text-white"
          data-testid="start-debate-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <MessageCircle className="w-4 h-4 mr-2" />
          )}
          Lancer le débat
        </Button>
      </div>

      <Button
        variant="ghost"
        onClick={() => {
          setTopicSubmitted(false);
          setDebatePhase("topic");
          setSuggestedPhilosophers([]);
          setSelectedPhilosophers([]);
        }}
        className="text-gray-500"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Changer de sujet
      </Button>
    </div>
  );

  // Debate phase
  const renderDebate = () => (
    <div className="max-w-4xl mx-auto" data-testid="debate-section">
      <div className="mb-4 p-4 bg-[#1A365D] rounded-lg text-white">
        <h2 className="text-xl font-medium mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
          Débat : {topic}
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-sm opacity-75">Participants :</span>
          {selectedPhilosophers.map((phil, idx) => (
            <Badge key={phil.id} className="bg-white/20">
              {phil.name}
            </Badge>
          ))}
        </div>
      </div>

      <Card className="mb-4">
        <ScrollArea className="h-[400px] p-4">
          <div className="space-y-4">
            {debateMessages.map((msg, idx) => {
              const isStudent = msg.role === "student";
              const philIndex = selectedPhilosophers.findIndex(p => p.id === msg.philosopher_id);
              
              return (
                <div
                  key={idx}
                  className={`flex gap-3 ${isStudent ? 'flex-row-reverse' : ''}`}
                  data-testid={`debate-message-${idx}`}
                >
                  {isStudent ? (
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-gray-600" />
                    </div>
                  ) : (
                    <div className="flex-shrink-0">
                      {getPhilosopherAvatar(msg.philosopher_id) ? (
                        <img 
                          src={getPhilosopherAvatar(msg.philosopher_id)} 
                          alt={msg.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`w-10 h-10 rounded-full ${getPhilosopherColor(philIndex)} flex items-center justify-center text-white font-bold`}>
                          {msg.name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className={`flex-1 ${isStudent ? 'text-right' : ''}`}>
                    <p className={`text-sm font-medium ${isStudent ? 'text-gray-600' : 'text-[#1A365D]'}`}>
                      {msg.name}
                    </p>
                    <div className={`mt-1 p-3 rounded-lg ${
                      isStudent 
                        ? 'bg-[#1A365D] text-white rounded-br-none ml-auto max-w-[80%]' 
                        : 'bg-gray-100 text-gray-800 rounded-bl-none'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {loading && (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Les philosophes réfléchissent...</span>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </Card>

      {/* Student input */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-2">
            <Input
              placeholder="Posez une question ou partagez votre réflexion..."
              value={studentQuestion}
              onChange={(e) => setStudentQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && askQuestion()}
              disabled={loading}
              className="flex-1"
              data-testid="student-question-input"
            />
            <Button
              onClick={askQuestion}
              disabled={loading || !studentQuestion.trim()}
              className="bg-[#1A365D] hover:bg-[#132745]"
              data-testid="send-question-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            <HelpCircle className="w-3 h-3 inline mr-1" />
            Intervenez pour poser des questions, demander des précisions ou challenger les arguments
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-between mt-4">
        <Button
          variant="outline"
          onClick={() => {
            setDebateStarted(false);
            setDebatePhase("selection");
            setDebateMessages([]);
          }}
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Nouveau débat
        </Button>
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
        >
          Terminer
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="debate-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
          <span className="navbar-logo hidden sm:block">SophIA</span>
          <Badge className="ml-2 bg-[#D4AF37] text-[#0F172A]">Débat Philosophique</Badge>
        </div>
      </nav>

      <main className="px-4 md:px-8 py-6 page-enter">
        {debatePhase === "topic" && renderTopicInput()}
        {debatePhase === "selection" && renderPhilosopherSelection()}
        {debatePhase === "debate" && renderDebate()}
      </main>
    </div>
  );
};

export default DebatePage;
